import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const LOGO_NAV = "/images/dea-wordmark.png";
const LOGO_HERO = "/images/dea-wordmark.png";

export default function PrivacyPolicy() {
  return (
    <>
      <SEO 
        title="Privacy Policy | Dea"
        description="Privacy Policy for AmbitionGPT - The Ambition Brand"
      />
      <div className="min-h-screen bg-[#FFFCF7]">
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-[#FFFCF7]/95 backdrop-blur-lg border-b border-[#EAD8C2]/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_NAV} alt="Dea" className="h-10 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-[#AA9283] hover:text-[#726158]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <main className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              <div className="text-center mb-12">
                <h1 className="font-serif text-4xl sm:text-5xl text-[#726158] mb-4">
                  Privacy Policy
                </h1>
                <p className="text-[#AA9283]">
                  The Ambition Brand / AmbitionGPT
                </p>
                <p className="text-sm text-[#B1897C] mt-2">
                  hello@theambitionplanner.com | beambitious.chat
                </p>
              </div>

              <div className="glass-card rounded-2xl p-8 space-y-8">
                {/* What We Collect */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">What We Collect</h2>
                  <ul className="space-y-2 text-[#AA9283]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Account info:</span>
                      <span>name, email, encrypted password</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Payment info:</span>
                      <span>processed by Stripe — we never store your card details</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Coaching data:</span>
                      <span>messages to Dea, goals, check-ins, and reflections you share</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Usage data:</span>
                      <span>login times, device/browser type, IP address</span>
                    </li>
                  </ul>
                </section>

                {/* How We Use It */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">How We Use It</h2>
                  <ul className="space-y-2 text-[#AA9283] list-disc list-inside">
                    <li>To deliver your personalized coaching experience through Dea</li>
                    <li>To send transactional emails (receipts, password resets, trial sequences)</li>
                    <li>To improve our AI — all data is anonymized before use</li>
                    <li>To process payments and prevent fraud</li>
                  </ul>
                </section>

                {/* Who We Share It With */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Who We Share It With</h2>
                  <p className="text-[#AA9283] mb-4">
                    We do not sell your data. We share only what's necessary to run our service:
                  </p>
                  <ul className="space-y-1 text-[#AA9283] list-disc list-inside">
                    <li>Stripe (payments)</li>
                    <li>Resend (email delivery)</li>
                    <li>OpenAI (AI processing)</li>
                    <li>Emergent.sh (hosting)</li>
                  </ul>
                </section>

                {/* Data Retention & Deletion */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Data Retention & Deletion</h2>
                  <ul className="space-y-2 text-[#AA9283]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Active accounts:</span>
                      <span>data kept while your account is active</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Trial users:</span>
                      <span>data retained so you can return and upgrade; your $9 credit never expires</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Deleted accounts:</span>
                      <span>data permanently removed within 30 days</span>
                    </li>
                  </ul>
                  <p className="text-[#AA9283] mt-4">
                    To delete your account: email <a href="mailto:hello@theambitionplanner.com" className="text-[#CAAB84] hover:underline">hello@theambitionplanner.com</a>
                  </p>
                </section>

                {/* California Residents (CCPA) */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">California Residents (CCPA)</h2>
                  <p className="text-[#AA9283]">
                    You have the right to know what data we collect, request deletion, and opt out of sale. We do not sell personal information. To exercise any right, email <a href="mailto:hello@theambitionplanner.com" className="text-[#CAAB84] hover:underline">hello@theambitionplanner.com</a>.
                  </p>
                </section>

                {/* Security */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Security</h2>
                  <p className="text-[#AA9283]">
                    We use SSL/TLS encryption, secure password hashing, and limited access controls. No internet transmission is 100% secure — use our service at your own discretion.
                  </p>
                </section>

                {/* Children */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Children</h2>
                  <p className="text-[#AA9283]">
                    AmbitionGPT is not intended for users under 18. We do not knowingly collect data from minors.
                  </p>
                </section>

                {/* Changes to This Policy */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Changes to This Policy</h2>
                  <p className="text-[#AA9283]">
                    We may update this policy with notice via email or in-app notification. Continued use after changes constitutes acceptance.
                  </p>
                </section>

                {/* Questions */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Questions?</h2>
                  <p className="text-[#AA9283]">
                    <a href="mailto:hello@theambitionplanner.com" className="text-[#CAAB84] hover:underline">hello@theambitionplanner.com</a>
                  </p>
                </section>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-12 border-t border-[#EAD8C2] bg-[#FFFCF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <img src={LOGO_HERO} alt="Dea" className="h-12 w-auto" />
              <div className="flex items-center gap-4">
                <Link to="/privacy" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-[#B1897C]">|</span>
                <Link to="/terms" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                  Terms of Service
                </Link>
                <span className="text-[#B1897C]">|</span>
                <p className="text-[#B1897C] text-sm">
                  © 2026 The Ambition Brand. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
